import { Box, Chip, MenuItem, Select } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { determineArrayUpdateOperation } from '../../../../../utils/determineArrayUpdateOperation';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';

const getTags = graphql(`
	query tags_getTags($activityId: UUID!) {
		availableTags: listTag {
			id
			name
		}
		assignedTags: listTag(filter: { activities: { id: { eq: $activityId } } }) {
			id
			name
		}
	}
`);

const updateTags = graphql(`
	mutation UpdateActivity($activityId: UUID!, $data: ActivityUpdateInput!) {
		updateActivity(by: { id: $activityId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

export const tagsEditDrawerSchema = z.object({
	activityId: z.string(),
});

const useUpdateTags = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { mutate } = useApiMutation(updateTags, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.tags.success.update' }),
					error: intl.formatMessage({ id: 'widget.tags.error.update' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});

	return mutate;
};

export const TagsEditDrawer: FC<z.infer<typeof tagsEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { data: tagsCollection, isFetched, isSuccess } = useApiQuery(getTags, { variables: { activityId } });
	const [tags, setTags] = useState<string[]>([]);
	const updateTags = useUpdateTags();

	const handleUpdateTags = () => {
		const initialTags = (tagsCollection?.assignedTags ?? []).map(({ id }) => id);
		const tagsOperations = determineArrayUpdateOperation(initialTags, tags);
		// Prevent firing empty update mutation
		if (tagsOperations.length > 0) {
			updateTags({ activityId, data: { tags: tagsOperations } });
		}
	};

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (isFetched && isSuccess && tagsCollection.assignedTags) {
			setTags(tagsCollection.assignedTags.map(({ id }) => id));
		}
	}, [isFetched, isSuccess, tagsCollection]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.tags.title' })} />
			<Select
				fullWidth
				multiple
				value={tags}
				renderValue={(tags) => {
					return (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{tags.map((id) => {
								const tag = tagsCollection?.availableTags.find((f) => f.id === id);

								if (tag) {
									return <Chip key={tag.id} label={tag.name} variant={'outlined'} />;
								}
								return null;
							})}
						</Box>
					);
				}}
				onChange={({ target: { value } }) => setTags(value as string[])}
				onClose={() => handleUpdateTags()}
			>
				{tagsCollection?.availableTags.map(({ id, name }) => (
					<MenuItem value={id} key={id}>
						{name}
					</MenuItem>
				))}
			</Select>
		</DrawerWrapper>
	);
};
